import { Button, Dropdown, ScrollArea, Search, Switch, Tabs, TabsList, TabsTrigger } from '@ibsys/kit';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronDown, SearchIcon } from 'lucide-react';
import { useHasPermission } from '@/entities/profile';
import { useAllAttributeGroups } from '@/entities/attributes';
import { useFormGuardActions } from '@/entities/formGuard';
import { AttributeFormSection } from './ui/AttributeFormSection';
import { AttributesList } from './ui/AttributeList/AttributesList';
import { MasterDetailLayout } from '@/shared/ui';

export const AttributesPage: FC = () => {
  const { t } = useTranslation(['common', 'attributes']);
  const allowAdmin = useHasPermission('ADMIN_ATTRIBUTE');
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const { setIsDirty } = useFormGuardActions();

  // Вкладка нужна каждой заведённой группе, поэтому набор берётся целиком, а не первым окном выдачи
  const { data: groupsData, isLoading: isLoadingGroups } = useAllAttributeGroups();

  const groups = useMemo(() => groupsData?.attributeGroups ?? [], [groupsData]);

  // Get active tab from search params
  // Empty string or undefined means "Все" tab is selected
  const activeTab = searchParams.get('activeTab') || undefined;

  const handleCreate = () => {
    // Preserve query parameters when navigating to create page
    const params = new URLSearchParams(searchParams);
    navigate(`/attributes/create?${params.toString()}`);
  };

  const handleBack = () => {
    // Возврат сохраняет выбранную группу и прочие условия отбора списка
    const params = new URLSearchParams(searchParams);
    navigate(`/attributes?${params.toString()}`);
  };

  const handleTabChange = (value: string) => {
    setSearchParams((prev) => {
      // If "Все" tab is selected (empty string), remove activeTab param
      if (value === '') {
        prev.delete('activeTab');
      } else {
        prev.set('activeTab', value);
      }
      return prev;
    });
  };

  // Опции для Dropdown на мобильном
  const dropdownOptions = useMemo(
    () => [
      {
        type: 'item' as const,
        key: '',
        title: t('common:all'),
        variant: !activeTab ? ('active' as const) : undefined,
        onClick: () => handleTabChange(''),
      },
      ...groups.map((g) => ({
        type: 'item' as const,
        key: g.code,
        title: g.name,
        variant: activeTab === g.code ? ('active' as const) : undefined,
        onClick: () => handleTabChange(g.code),
      })),
    ],
    [groups, activeTab, t],
  );

  // Метка активной группы для триггера Dropdown
  const activeGroupLabel = activeTab
    ? (groups.find((g) => g.code === activeTab)?.name ?? t('common:all'))
    : t('common:all');

  return (
    <MasterDetailLayout
      isDetailOpen={Boolean(id)}
      onBack={handleBack}
      toolbar={
        <>
          {/* Шапка: поиск и кнопка добавления */}
          <div className="flex items-center gap-4 flex-wrap">
            <Search
              className="flex-1 min-w-40"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('search')}
            />
            <Button variant={'outline'} icon={<SearchIcon />}>
              <span className={'hidden md:inline'}>{t('common:search')}</span>
            </Button>
            {allowAdmin && (
              <Button onClick={handleCreate} className="ml-auto">
                {t('attributes:addAttribute')}
              </Button>
            )}
          </div>

          {/* Фильтр по группам: Dropdown на мобильном, Tabs на десктопе */}
          {!isLoadingGroups && (
            <Tabs className="hidden md:block" value={activeTab || ''} onValueChange={handleTabChange}>
              <TabsList>
                <ScrollArea orientation={'horizontal'} classNames={{ horizontalBar: 'h-1.5' }}>
                  <div className={'flex'}>
                    <TabsTrigger value="">{t('common:all')}</TabsTrigger>
                    {groups.map((group) => (
                      <TabsTrigger key={group.code} value={group.code}>
                        {group.name}
                      </TabsTrigger>
                    ))}
                  </div>
                </ScrollArea>
              </TabsList>
            </Tabs>
          )}

          <div className="flex gap-2 items-center">
            <Switch value={showDeleted} onChange={setShowDeleted} label={t('common:showDeleted')} />
            {!isLoadingGroups && (
              <div className="md:hidden grow-0 mr-auto">
                <Dropdown options={dropdownOptions}>
                  <Button variant="ghost" className="w-full justify-between">
                    {activeGroupLabel}
                    <ChevronDown />
                  </Button>
                </Dropdown>
              </div>
            )}
          </div>
        </>
      }
      list={<AttributesList showDeleted={showDeleted} search={search} />}
      detail={<AttributeFormSection attributeCode={id} key={id} onUnsavedChange={(dirty) => setIsDirty(dirty)} />}
    />
  );
};
